import React, { useState, useEffect, useMemo } from "react";
import BreadCrumb from "../../Components/Common/BreadCrumb";
import { Spinner } from "../../Components/Common/LoadingStates";
import {
  getFeedbackCategories,
  getFeedbackSuggestions,
  getFeedbackSuggestionById,
  getFeedbackBugReports,
  getFeedbackDoNotLike,
  createFeedbackCategory,
  deleteFeedbackSuggestion,
  deleteFeedbackBugReport,
  updateFeedbackSuggestionStatus,
  updateFeedbackStatus,
  moveFeedbackType,
} from "../../helpers/fakebackend_helper";
import { useCachedRequest, invalidateCache } from "../../helpers/cachedFetch";
import { usePermission } from "../../helpers/usePermission";

const Feedback = ({ embedded = false } = {}) => {
  document.title = "Feedback | BiorGov - BiorGov Admin & Dashboard";
  const can = usePermission();
  const canUpdate = can("feedback.update");
  const canDelete = can("feedback.delete");

  const [activeTab, setActiveTab] = useState("suggestions");
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [selectedSuggestion, setSelectedSuggestion] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [sortBy, setSortBy] = useState("upvote_count");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [categoryForm, setCategoryForm] = useState({ name: "", display_order: "" });
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState("");
  const [deletingId, setDeletingId] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  // Which bucket every tab shows: active (the working queue), archive
  // ("maybe later"), or spam (scammers/trash). Archive + spam are hidden from
  // the active view and live in their own places.
  const [bucket, setBucket] = useState("active");
  // Move-between-places state. `moveItem` is the row being re-filed.
  const [moveItem, setMoveItem] = useState(null);
  const [moveToType, setMoveToType] = useState("");
  const [moveTitle, setMoveTitle] = useState("");
  const [moveLoading, setMoveLoading] = useState(false);
  const [moveError, setMoveError] = useState("");
  const [moveNotice, setMoveNotice] = useState("");

  // The label says what the app does with the suggestion (Feedback › Vote in
  // the app; ADMIN_PANEL_MAP.md M11). Approved, Rejected and Done are public;
  // the current app lists all three as open and votable and does not draw the
  // status yet, so Done / Rejected still read as open there.
  const SUGGESTION_STATUSES = [
    { value: "pending", label: "Pending", app: "hidden in app" },
    { value: "in_review", label: "In Review", app: "hidden in app" },
    { value: "approved", label: "Approved", app: "public in app" },
    { value: "rejected", label: "Rejected", app: "public in app, shown as open" },
    { value: "hide", label: "Hide", app: "hidden in app" },
    { value: "done", label: "Done", app: "public in app, shown as open" },
    { value: "spam", label: "Spam", app: "hidden in app" },
  ];

  const getStatusLabel = (value) =>
    SUGGESTION_STATUSES.find((s) => s.value === value)?.label || (value || "pending");

  // The three places a submission can live. All three are rows in the same
  // `feedback` table keyed on `type`, which is why moving one is cheap.
  const PLACES = [
    { type: "suggestion", tab: "suggestions", label: "Suggestions" },
    { type: "bug_report", tab: "bug-reports", label: "Bug Reports" },
    { type: "do_not_like", tab: "do-not-like", label: "Do Not Like" },
  ];
  const placeOfTab = (tab) => PLACES.find((p) => p.tab === tab);
  const placeOfType = (type) => PLACES.find((p) => p.type === type);
  // Mirrors feedback.service.getStatusesVisibleInApp() — these statuses are
  // PUBLIC for suggestions (the mobile Vote tab reads them), so moving an
  // already-approved bug report into Suggestions would publish it. The backend
  // resets such a row to `in_review`; we warn about it before they confirm.
  const APP_VISIBLE_STATUSES = ["approved", "rejected", "done"];

  // The move endpoint always takes the FEEDBACK id. On the Suggestions tab
  // `item.id` is a suggestions.id, so the feedback id comes off `feedback_id`.
  const feedbackIdOf = (item) =>
    activeTab === "suggestions" ? (item?.feedback_id ?? null) : (item?.id ?? null);

  // The axios response interceptor already unwraps `.data` — `res` IS the JSON
  // body. The only quirk: GET /feedback/categories returns `{ data: [...] }`
  // (the controller wraps the list), so for THAT endpoint we read `res.data`.
  const categoriesReq = useCachedRequest({
    key: "feedback:categories",
    fetcher: () => getFeedbackCategories().then((res) => (Array.isArray(res?.data) ? res.data : [])).catch(() => []),
  });
  const categories = categoriesReq.data || [];

  const suggestionsReq = useCachedRequest({
    key: `feedback:suggestions:${page}:${pageSize}:${sortBy}:${sortOrder}:${categoryFilter}:${bucket}`,
    fetcher: () => {
      const params = { page, limit: pageSize, sort: sortBy, order: sortOrder, bucket };
      if (categoryFilter) params.category_id = categoryFilter;
      return getFeedbackSuggestions(params);
    },
    deps: [page, pageSize, sortBy, sortOrder, categoryFilter, bucket],
    enabled: activeTab === "suggestions",
  });

  const bugReportsReq = useCachedRequest({
    key: `feedback:bugs:${page}:${pageSize}:${bucket}`,
    fetcher: () => getFeedbackBugReports({ page, limit: pageSize, bucket }),
    deps: [page, pageSize, bucket],
    enabled: activeTab === "bug-reports",
  });

  const doNotLikeReq = useCachedRequest({
    key: `feedback:dislike:${page}:${pageSize}:${bucket}`,
    fetcher: () => getFeedbackDoNotLike({ page, limit: pageSize, bucket }),
    deps: [page, pageSize, bucket],
    enabled: activeTab === "do-not-like",
  });

  const fetchCategories = () => { invalidateCache("feedback:categories"); return categoriesReq.refetch(); };
  const fetchSuggestions = () => suggestionsReq.refetch();
  const fetchBugReports = () => bugReportsReq.refetch();
  const fetchDoNotLike  = () => doNotLikeReq.refetch();

  const activeReq = activeTab === "suggestions" ? suggestionsReq
                   : activeTab === "bug-reports" ? bugReportsReq
                   : activeTab === "do-not-like" ? doNotLikeReq
                   : null;
  const loading = activeReq?.loading || false;
  const suggestions = useMemo(() => Array.isArray(suggestionsReq.data?.suggestions) ? suggestionsReq.data.suggestions : [], [suggestionsReq.data]);
  const bugReports  = useMemo(() => Array.isArray(bugReportsReq.data?.bug_reports) ? bugReportsReq.data.bug_reports : [], [bugReportsReq.data]);
  const doNotLike   = useMemo(() => Array.isArray(doNotLikeReq.data?.do_not_like) ? doNotLikeReq.data.do_not_like : [], [doNotLikeReq.data]);
  const total = activeTab === "suggestions" ? (suggestionsReq.data?.pagination?.total ?? suggestions.length)
              : activeTab === "bug-reports" ? (bugReportsReq.data?.pagination?.total ?? bugReports.length)
              : activeTab === "do-not-like" ? (doNotLikeReq.data?.pagination?.total ?? doNotLike.length)
              : 0;
  useEffect(() => {
    if (activeReq?.error && !activeReq?.data) {
      setError(typeof activeReq.error === "string" ? activeReq.error : activeReq.error?.message || "Failed to load");
    }
  }, [activeReq?.error, activeReq?.data]);

  const formatDate = (iso) => {
    if (!iso) return "-";
    try {
      return new Date(iso).toLocaleString(undefined, {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
    } catch (_) {
      return iso;
    }
  };

  const handleViewDetails = async (id) => {
    try {
      let res;
      if (activeTab === "suggestions") {
        res = await getFeedbackSuggestionById(id);
      } else {
        // For bug reports and do-not-like, we already have the data
        const currentData = getCurrentData();
        const item = currentData.find((i) => i.id === id);
        if (item) {
          setSelectedSuggestion(item);
          setShowModal(true);
          return;
        }
        // Fallback: try to get from feedback service
        res = await getFeedbackSuggestionById(id);
      }
      // Interceptor already unwraps — `res` IS the body for the by-id endpoint.
      setSelectedSuggestion(res);
      setShowModal(true);
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to load details"
      );
    }
  };

  const handleCreateCategory = async (e) => {
    e.preventDefault();
    if (!categoryForm.name.trim()) {
      setCategoryError("Category name is required");
      return;
    }

    try {
      setCategoryLoading(true);
      setCategoryError("");
      const payload = {
        name: categoryForm.name.trim(),
      };
      if (categoryForm.display_order) {
        payload.display_order = parseInt(categoryForm.display_order);
      }

      await createFeedbackCategory(payload);
      setCategoryForm({ name: "", display_order: "" });
      setShowCategoryModal(false);
      await fetchCategories(); // Refresh categories list
    } catch (err) {
      setCategoryError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to create category"
      );
    } finally {
      setCategoryLoading(false);
    }
  };

  const getCurrentData = () => {
    switch (activeTab) {
      case "suggestions":
        return suggestions;
      case "bug-reports":
        return bugReports;
      case "do-not-like":
        return doNotLike;
      default:
        return [];
    }
  };

  const handleUpdateSuggestionStatus = async (suggestionId, newStatus) => {
    try {
      setUpdatingStatusId(suggestionId);
      setError("");
      await updateFeedbackSuggestionStatus(suggestionId, newStatus);
      if (selectedSuggestion?.id === suggestionId) {
        setSelectedSuggestion((prev) => (prev ? { ...prev, status: newStatus } : null));
      }
      await fetchSuggestions();
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || "Failed to update status"
      );
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleDelete = async (item) => {
    const isSuggestion = activeTab === "suggestions";
    const label = isSuggestion ? "suggestion" : "bug report";
    if (!window.confirm(`Are you sure you want to delete this ${label}?`)) return;
    try {
      setDeletingId(item.id);
      setError("");
      if (isSuggestion) {
        await deleteFeedbackSuggestion(item.id);
      } else {
        await deleteFeedbackBugReport(item.id);
      }
      setShowModal(false);
      setSelectedSuggestion(null);
      if (activeTab === "suggestions") {
        await fetchSuggestions();
      } else {
        await fetchBugReports();
      }
    } catch (err) {
      setError(
        typeof err === "string"
          ? err
          : err?.message || `Failed to delete ${label}`
      );
    } finally {
      setDeletingId(null);
    }
  };

  const refetchActive = () => {
    if (activeTab === "suggestions") return fetchSuggestions();
    if (activeTab === "bug-reports") return fetchBugReports();
    if (activeTab === "do-not-like") return fetchDoNotLike();
  };

  // One-tap status change used for Spam / Restore. Suggestions use their
  // dedicated status endpoint; bug reports / do-not-like use the generic one.
  const handleMarkStatus = async (item, status) => {
    try {
      setUpdatingStatusId(item.id);
      setError("");
      if (activeTab === "suggestions") {
        await updateFeedbackSuggestionStatus(item.id, status);
      } else {
        await updateFeedbackStatus(item.id, status);
      }
      setShowModal(false);
      setSelectedSuggestion(null);
      await refetchActive();
    } catch (err) {
      setError(typeof err === "string" ? err : err?.message || "Failed to update status");
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const openMoveModal = (item) => {
    setMoveItem(item);
    // Default to the first other place, so a single click + Confirm is enough
    // when there are only two options anyway.
    const others = PLACES.filter((p) => p.tab !== activeTab);
    setMoveToType(others[0]?.type || "");
    setMoveTitle("");
    setMoveError("");
    setMoveNotice("");
  };

  const closeMoveModal = () => {
    setMoveItem(null);
    setMoveToType("");
    setMoveTitle("");
    setMoveError("");
  };

  const handleMove = async () => {
    if (!moveItem || !moveToType) return;
    const feedbackId = feedbackIdOf(moveItem);
    if (!feedbackId) {
      setMoveError(
        "Could not resolve this row's feedback id — reload the page and try again."
      );
      return;
    }
    try {
      setMoveLoading(true);
      setMoveError("");
      const res = await moveFeedbackType(feedbackId, moveToType, moveTitle.trim() || undefined);
      // Interceptor unwraps `.data`, so `res` IS the body: { data: {...} }.
      const info = res?.data || {};
      const toLabel = placeOfType(info.to_type || moveToType)?.label || moveToType;

      // Tell them exactly what was reconciled rather than silently changing it.
      const notes = [];
      if (info.moved === false) {
        notes.push("it was already there — nothing changed");
      } else {
        if (info.title_derived && info.title) notes.push(`title set to "${info.title}"`);
        if (info.status_reset_from) {
          notes.push(
            `status reset from ${getStatusLabel(info.status_reset_from)} to In Review so it isn't published in the app until you approve it`
          );
        }
        if (info.suggestion_row_reused) notes.push("previous upvotes and views restored");
      }
      setMoveNotice(
        `Moved to ${toLabel}${notes.length ? ` — ${notes.join("; ")}` : ""}.`
      );

      closeMoveModal();
      setShowModal(false);
      setSelectedSuggestion(null);
      // The row left this tab and appeared in another, so every feedback list
      // is now stale, not just the active one.
      invalidateCache("feedback:");
      await refetchActive();
    } catch (err) {
      setMoveError(typeof err === "string" ? err : err?.message || "Failed to move");
    } finally {
      setMoveLoading(false);
    }
  };

  const renderImageGallery = (attachments) => {
    // Ensure attachments is always an array
    const attachmentsArray = Array.isArray(attachments) 
      ? attachments 
      : (attachments && typeof attachments === 'object' ? [attachments] : []);
    
    if (!attachmentsArray || attachmentsArray.length === 0) return null;
    return (
      <div className="flex flex-wrap gap-2 mt-2">
        {attachmentsArray.map((att, idx) => (
          <img
            key={idx}
            src={att.url || att.file_url || att}
            alt={`Attachment ${idx + 1}`}
            className="w-20 h-20 object-cover rounded border border-gray-700 cursor-pointer hover:opacity-80"
            onClick={() => window.open(att.url || att.file_url || att, "_blank")}
          />
        ))}
      </div>
    );
  };

  return (
    <div className={embedded ? "min-w-0" : "min-h-screen min-w-0"}>
      <div className={embedded ? "w-full min-w-0" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 w-full min-w-0"}>
        {!embedded && <BreadCrumb title="Feedback" pageTitle="Management" />}

        {/* Tabs */}
        <div className="bl-glass mb-6 min-w-0">
          <div className="border-b border-gray-700 flex items-center justify-between flex-wrap gap-2 pr-3">
            <nav className="flex flex-wrap -mb-px gap-0">
              <button
                onClick={() => {
                  setActiveTab("suggestions");
                  setPage(1);
                }}
                className={`px-6 py-4 text-sm font-normal border-b-2 transition-colors ${
                  activeTab === "suggestions"
                    ? "border-bl-accent text-gray-100"
                    : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-700"
                }`}
              >
                Suggestions
              </button>
              <button
                onClick={() => {
                  setActiveTab("bug-reports");
                  setPage(1);
                }}
                className={`px-6 py-4 text-sm font-normal border-b-2 transition-colors ${
                  activeTab === "bug-reports"
                    ? "border-bl-accent text-gray-100"
                    : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-700"
                }`}
              >
                Bug Reports
              </button>
              <button
                onClick={() => {
                  setActiveTab("do-not-like");
                  setPage(1);
                }}
                className={`px-6 py-4 text-sm font-normal border-b-2 transition-colors ${
                  activeTab === "do-not-like"
                    ? "border-bl-accent text-gray-100"
                    : "border-transparent text-gray-400 hover:text-gray-200 hover:border-gray-700"
                }`}
              >
                Do Not Like
              </button>
            </nav>
            {/* Bucket selector: Active queue · Archive (maybe later) · Spam (trash) */}
            <div className="inline-flex rounded-md border border-gray-700 overflow-hidden text-xs">
              {[
                { id: "active", label: "Active", icon: "ri-inbox-line" },
                { id: "archive", label: "Archive", icon: "ri-archive-line" },
                { id: "spam", label: "Spam", icon: "ri-spam-2-line" },
              ].map((b) => {
                const on = bucket === b.id;
                const tint = b.id === "spam" ? "bg-red-500/20 text-red-300" : b.id === "archive" ? "bg-amber-500/16 text-amber-300" : "bg-bl-accent text-white";
                return (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => { setBucket(b.id); setPage(1); }}
                    className={`px-3 py-1.5 font-medium flex items-center gap-1.5 transition-colors ${on ? tint : "bg-gray-800 text-gray-400 hover:text-gray-200"}`}
                  >
                    <i className={b.icon}></i>
                    {b.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Filters for Suggestions */}
          {activeTab === "suggestions" && (
            <div className="p-4 border-b border-gray-700 flex flex-wrap gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-300">
                    Category:
                  </label>
                  <select
                    value={categoryFilter}
                    onChange={(e) => {
                      setCategoryFilter(e.target.value);
                      setPage(1);
                    }}
                    className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                  >
                    <option value="">All Categories ({categories.length})</option>
                    {categories.length > 0 ? (
                      categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))
                    ) : (
                      <option disabled>Loading categories...</option>
                    )}
                  </select>
                </div>
                {canUpdate && (
                  <button
                    onClick={() => setShowCategoryModal(true)}
                    className="px-3 py-2 text-sm font-normal text-white bg-bl-accent hover:bg-bl-accent-light rounded-md transition-colors flex items-center gap-2"
                  >
                    <i className="ri-add-line"></i>
                    Add Category
                  </button>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-300">
                  Sort By:
                </label>
                <select
                  value={sortBy}
                  onChange={(e) => {
                    setSortBy(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                >
                  <option value="upvote_count">Upvotes</option>
                  <option value="view_count">Views</option>
                  <option value="created_at">Date Created</option>
                  <option value="updated_at">Last Updated</option>
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-300">
                  Order:
                </label>
                <select
                  value={sortOrder}
                  onChange={(e) => {
                    setSortOrder(e.target.value);
                    setPage(1);
                  }}
                  className="px-3 py-2 text-sm border border-gray-700 rounded-md shadow-sm bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                >
                  <option value="DESC">Descending</option>
                  <option value="ASC">Ascending</option>
                </select>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="p-6">
            {error && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                {error}
              </div>
            )}

            {moveNotice && (
              <div className="mb-4 p-3 bg-sky-500/10 border border-sky-500/30 rounded-lg text-sm text-sky-300 flex items-start justify-between gap-3">
                <span className="min-w-0">{moveNotice}</span>
                <button
                  onClick={() => setMoveNotice("")}
                  className="text-sky-400 hover:text-sky-200 transition-colors shrink-0"
                  title="Dismiss"
                >
                  <i className="ri-close-line"></i>
                </button>
              </div>
            )}

            {loading && (
              <div className="text-sm text-gray-400 mb-4 flex items-center gap-2">
                <Spinner size={14} />
                <span>Loading…</span>
              </div>
            )}

            <div className="overflow-x-auto overflow-y-hidden">
              <table className="w-full table-fixed divide-y divide-gray-700">
                <thead className="bg-bl-accent/5">
                  <tr>
                    {activeTab === "suggestions" && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-[min(200px,25%)] min-w-0">
                          Title
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Status
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Upvotes
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Views
                        </th>
                      </>
                    )}
                    {(activeTab === "bug-reports" ||
                      activeTab === "do-not-like") && (
                      <>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider w-[min(220px,28%)] min-w-0">
                          Description
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Email
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          Platform
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                          App Version
                        </th>
                      </>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider whitespace-nowrap w-[min(180px,22%)]">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-gray-800 divide-y divide-gray-700">
                  {getCurrentData().length === 0 && !loading ? (
                    <tr>
                      <td
                        colSpan={
                          activeTab === "suggestions" ? 7 : 6
                        }
                        className="px-4 py-8 text-center text-sm text-gray-400"
                      >
                        No data found
                      </td>
                    </tr>
                  ) : (
                    getCurrentData().map((item) => (
                      <tr
                        key={item.id}
                        className="hover:bg-bl-accent/10/50 transition-colors"
                      >
                        {activeTab === "suggestions" && (
                          <>
                            <td className="px-4 py-3 text-sm font-medium text-gray-100 min-w-0 break-words">
                              {item.title || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.category?.name || item.category_name || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-700 text-gray-300">
                                {getStatusLabel(item.status)}
                              </span>
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.upvote_count || 0}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.view_count || 0}
                            </td>
                          </>
                        )}
                        {(activeTab === "bug-reports" ||
                          activeTab === "do-not-like") && (
                          <>
                            <td className="px-4 py-3 text-sm text-gray-300 min-w-0 break-words">
                              {item.description || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.email || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.platform || "-"}
                            </td>
                            <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                              {item.app_version || "-"}
                            </td>
                          </>
                        )}
                        <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-300">
                          {formatDate(item.created_at)}
                        </td>
                        <td className="px-4 py-3 text-sm min-w-0">
                          <div className="flex flex-wrap items-center gap-2">
                            <button
                              onClick={() => handleViewDetails(item.id)}
                              className="text-gray-400 hover:text-gray-100 transition-colors whitespace-nowrap"
                            >
                              View Details
                            </button>
                            {canUpdate && (
                              <button
                                onClick={() => openMoveModal(item)}
                                className="text-sky-400 hover:text-sky-300 transition-colors whitespace-nowrap shrink-0"
                                title="Move to a different place — same post, filed under Bug Reports / Do Not Like / Suggestions"
                              >
                                ⇄ Move
                              </button>
                            )}
                            {canUpdate && bucket !== "active" && (
                              <button
                                onClick={() => handleMarkStatus(item, "pending")}
                                disabled={updatingStatusId === item.id}
                                className="text-emerald-400 hover:text-emerald-300 transition-colors disabled:opacity-50 whitespace-nowrap shrink-0"
                                title="Restore — back to the active queue"
                              >
                                {updatingStatusId === item.id ? "…" : "↩ Restore"}
                              </button>
                            )}
                            {canUpdate && bucket !== "archive" && (
                              <button
                                onClick={() => handleMarkStatus(item, "hide")}
                                disabled={updatingStatusId === item.id}
                                className="text-amber-400 hover:text-amber-300 transition-colors disabled:opacity-50 whitespace-nowrap shrink-0"
                                title="Archive — keep for later, out of the active queue"
                              >
                                {updatingStatusId === item.id ? "…" : "🗄 Archive"}
                              </button>
                            )}
                            {canUpdate && bucket !== "spam" && (
                              <button
                                onClick={() => handleMarkStatus(item, "spam")}
                                disabled={updatingStatusId === item.id}
                                className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 whitespace-nowrap shrink-0"
                                title="Spam — scammers / trash"
                              >
                                {updatingStatusId === item.id ? "…" : "🚫 Spam"}
                              </button>
                            )}
                            {canUpdate && activeTab === "suggestions" && (
                              <select
                                value={item.status || "pending"}
                                onChange={(e) =>
                                  handleUpdateSuggestionStatus(
                                    item.id,
                                    e.target.value
                                  )
                                }
                                disabled={updatingStatusId === item.id}
                                className="px-2 py-1 text-xs border border-gray-700 rounded bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent disabled:opacity-50"
                              >
                                {SUGGESTION_STATUSES.map((s) => (
                                  <option key={s.value} value={s.value}>
                                    {s.label} · {s.app}
                                  </option>
                                ))}
                              </select>
                            )}
                            {canDelete && (activeTab === "suggestions" ||
                              activeTab === "bug-reports") && (
                              <button
                                onClick={() => handleDelete(item)}
                                disabled={deletingId === item.id}
                                className="text-red-400 hover:text-red-300 transition-colors disabled:opacity-50 shrink-0"
                                title={
                                  activeTab === "suggestions"
                                    ? "Delete suggestion"
                                    : "Delete bug report"
                                }
                              >
                                {deletingId === item.id ? (
                                  "Deleting..."
                                ) : (
                                  <i className="ri-delete-bin-line text-base"></i>
                                )}
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {total > 0 && (
              <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mt-6 pt-4 border-t border-gray-700">
                <button
                  disabled={page === 1 || loading}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  ← Previous
                </button>
                <div className="text-sm text-gray-400">
                  Page {page} of {Math.max(1, Math.ceil(total / pageSize))} •{" "}
                  {total} total
                </div>
                <button
                  disabled={loading || page >= Math.max(1, Math.ceil(total / pageSize))}
                  onClick={() => setPage((p) => p + 1)}
                  className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next →
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      {showModal && selectedSuggestion && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bl-glass-strong max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex flex-wrap justify-between items-center gap-3">
              <h3 className="text-lg font-normal text-gray-100 break-words min-w-0 flex-1">
                {activeTab === "suggestions"
                  ? selectedSuggestion.title
                  : "Feedback Details"}
              </h3>
              <div className="flex flex-wrap items-center gap-2 shrink-0">
                {activeTab === "suggestions" && (
                  <div className="flex items-center gap-2">
                    <label className="text-xs text-gray-400 whitespace-nowrap">Status:</label>
                    <select
                      value={selectedSuggestion.status || "pending"}
                      onChange={(e) =>
                        handleUpdateSuggestionStatus(
                          selectedSuggestion.id,
                          e.target.value
                        )
                      }
                      disabled={updatingStatusId === selectedSuggestion.id}
                      className="px-3 py-1.5 text-sm border border-gray-700 rounded-md bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-gray-400"
                    >
                      {SUGGESTION_STATUSES.map((s) => (
                        <option key={s.value} value={s.value}>
                          {s.label} · {s.app}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                {canUpdate && (
                  <button
                    onClick={() => openMoveModal(selectedSuggestion)}
                    className="px-3 py-1.5 text-sm text-sky-400 hover:bg-sky-500/10 rounded-md transition-colors"
                    title="Move to a different place"
                  >
                    ⇄ Move
                  </button>
                )}
                {(activeTab === "suggestions" ||
                  activeTab === "bug-reports") && (
                  <button
                    onClick={() => handleDelete(selectedSuggestion)}
                    disabled={deletingId === selectedSuggestion.id}
                    className="px-3 py-1.5 text-sm text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-50"
                  >
                    {deletingId === selectedSuggestion.id
                      ? "Deleting..."
                      : "Delete"}
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowModal(false);
                    setSelectedSuggestion(null);
                  }}
                  className="text-gray-400 hover:text-gray-200 transition-colors"
                >
                  <i className="ri-close-line text-xl"></i>
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {activeTab === "suggestions" && (
                  <>
                    <div>
                      <label className="text-sm font-medium text-gray-300">
                        Title
                      </label>
                      <p className="mt-1 text-sm text-gray-100">
                        {selectedSuggestion.title}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300">
                        Category
                      </label>
                      <p className="mt-1 text-sm text-gray-100">
                        {selectedSuggestion.category?.name || selectedSuggestion.category_name || "-"}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300">
                        Upvotes
                      </label>
                      <p className="mt-1 text-sm text-gray-100">
                        {selectedSuggestion.upvote_count || 0}
                      </p>
                    </div>
                    <div>
                      <label className="text-sm font-medium text-gray-300">
                        Views
                      </label>
                      <p className="mt-1 text-sm text-gray-100">
                        {selectedSuggestion.view_count || 0}
                      </p>
                    </div>
                  </>
                )}
                <div>
                  <label className="text-sm font-medium text-gray-300">
                    Description
                  </label>
                  <p className="mt-1 text-sm text-gray-100 whitespace-pre-wrap">
                    {selectedSuggestion.description}
                  </p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      Email
                    </label>
                    <p className="mt-1 text-sm text-gray-100">
                      {selectedSuggestion.email || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      Platform
                    </label>
                    <p className="mt-1 text-sm text-gray-100">
                      {selectedSuggestion.platform || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      App Version
                    </label>
                    <p className="mt-1 text-sm text-gray-100">
                      {selectedSuggestion.app_version || "-"}
                    </p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      Created At
                    </label>
                    <p className="mt-1 text-sm text-gray-100">
                      {formatDate(selectedSuggestion.created_at)}
                    </p>
                  </div>
                </div>
                {selectedSuggestion.device_info && (
                  <div>
                    <label className="text-sm font-medium text-gray-300">
                      Device Info
                    </label>
                    <pre className="mt-1 text-xs text-gray-100 bg-bl-accent/5 p-3 rounded overflow-x-auto">
                      {JSON.stringify(
                        typeof selectedSuggestion.device_info === "string"
                          ? JSON.parse(selectedSuggestion.device_info)
                          : selectedSuggestion.device_info,
                        null,
                        2
                      )}
                    </pre>
                  </div>
                )}
                {selectedSuggestion.attachments && 
                  (Array.isArray(selectedSuggestion.attachments) 
                    ? selectedSuggestion.attachments.length > 0 
                    : Object.keys(selectedSuggestion.attachments).length > 0) && (
                    <div>
                      <label className="text-sm font-medium text-gray-300">
                        Attachments
                      </label>
                      {renderImageGallery(selectedSuggestion.attachments)}
                    </div>
                  )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Move Modal — re-file a submission that landed in the wrong place.
          z-[60] because the detail modal sits at z-50 and Move can be opened
          from inside it (Topbar is z-100, so we stay under that). */}
      {moveItem && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[60] flex items-center justify-center p-4">
          <div className="bl-glass-strong max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center gap-3">
              <h3 className="text-lg font-normal text-gray-100">Move this submission</h3>
              <button
                onClick={closeMoveModal}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>

            <div className="p-6 space-y-5">
              {moveError && (
                <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                  {moveError}
                </div>
              )}

              {/* What is being moved */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-1">
                  Moving
                </label>
                <p className="text-sm text-gray-100 break-words">
                  {moveItem.title || moveItem.description || `#${feedbackIdOf(moveItem)}`}
                </p>
                <p className="mt-1 text-xs text-gray-400">
                  Currently in{" "}
                  <span className="text-gray-200">{placeOfTab(activeTab)?.label}</span>
                  {" · "}feedback #{feedbackIdOf(moveItem) ?? "?"}
                </p>
              </div>

              {/* Destination */}
              <div>
                <label className="block text-xs font-medium text-gray-400 uppercase tracking-wider mb-2">
                  Move to
                </label>
                <div className="flex flex-col gap-2">
                  {PLACES.filter((p) => p.tab !== activeTab).map((p) => (
                    <label
                      key={p.type}
                      className={`flex items-center gap-3 px-3 py-2 rounded-md border cursor-pointer transition-colors ${
                        moveToType === p.type
                          ? "border-bl-accent bg-bl-accent/10 text-gray-100"
                          : "border-gray-700 bg-gray-800 text-gray-300 hover:border-gray-600"
                      }`}
                    >
                      <input
                        type="radio"
                        name="move-target"
                        value={p.type}
                        checked={moveToType === p.type}
                        onChange={() => setMoveToType(p.type)}
                        className="accent-bl-accent"
                      />
                      <span className="text-sm">{p.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Title — only Suggestions renders one, and only rows that
                  arrived without one need it filled in. */}
              {moveToType === "suggestion" && !String(moveItem.title || "").trim() && (
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-1">
                    Suggestion title <span className="text-gray-500">(optional)</span>
                  </label>
                  <input
                    type="text"
                    value={moveTitle}
                    onChange={(e) => setMoveTitle(e.target.value)}
                    maxLength={255}
                    placeholder="Leave blank to use the start of the description"
                    className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-500 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                  />
                  <p className="mt-1 text-xs text-gray-400">
                    Bug reports and Do Not Like are submitted without a title, but the
                    Suggestions list and the app's Vote tab both show one.
                  </p>
                </div>
              )}

              {/* Exactly what happens — no silent surprises */}
              <div className="p-3 rounded-md bg-bl-accent/5 border border-gray-700 text-xs text-gray-300 space-y-1.5">
                <p className="text-gray-200 font-medium">What carries over</p>
                <p>
                  Description, email, attachments, platform, app version, device info and
                  the original submission date all stay exactly as they are.
                </p>
                {moveToType === "suggestion" && (
                  <p>
                    Gains upvotes and views. If this post was in Suggestions before, its
                    previous counts come back.
                  </p>
                )}
                {activeTab === "suggestions" && moveToType !== "suggestion" && (
                  <p>
                    Its upvotes and views are kept but hidden, so moving it back to
                    Suggestions restores them. Nothing is deleted.
                  </p>
                )}
                {moveToType === "suggestion" &&
                  APP_VISIBLE_STATUSES.includes(moveItem.status || "pending") && (
                    <p className="text-amber-300">
                      Status is {getStatusLabel(moveItem.status)}, which is public for
                      suggestions — it will be reset to In Review so this doesn't appear in
                      the app's Vote tab until you approve it.
                    </p>
                  )}
              </div>
            </div>

            <div className="p-6 pt-0 flex gap-3 justify-end">
              <button
                type="button"
                onClick={closeMoveModal}
                className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleMove}
                disabled={moveLoading || !moveToType}
                className="px-4 py-2 text-sm font-normal text-white bg-bl-accent hover:bg-bl-accent-light rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {moveLoading
                  ? "Moving..."
                  : `Move to ${placeOfType(moveToType)?.label || "…"}`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Category Creation Modal */}
      {showCategoryModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bl-glass-strong max-w-md w-full">
            <div className="p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-normal text-gray-100">
                Create New Category
              </h3>
              <button
                onClick={() => {
                  setShowCategoryModal(false);
                  setCategoryForm({ name: "", display_order: "" });
                  setCategoryError("");
                }}
                className="text-gray-400 hover:text-gray-200 transition-colors"
              >
                <i className="ri-close-line text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleCreateCategory} className="p-6">
              {categoryError && (
                <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm text-red-400">
                  {categoryError}
                </div>
              )}
              <p className="mb-4 text-xs text-gray-400">
                Categories are the choices on the app's Feedback › Suggestion form. Bugs have their own
                Bug report tab in the app, so a bug category is not needed there.
              </p>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Category Name <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  placeholder="Enter category name..."
                  value={categoryForm.name}
                  onChange={(e) =>
                    setCategoryForm({ ...categoryForm, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-400 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-300 mb-1">
                  Display Order (Optional)
                </label>
                <input
                  type="number"
                  placeholder="Leave empty for auto"
                  value={categoryForm.display_order}
                  onChange={(e) =>
                    setCategoryForm({
                      ...categoryForm,
                      display_order: e.target.value,
                    })
                  }
                  min="0"
                  className="w-full px-3 py-2 border border-gray-700 rounded-md shadow-sm placeholder-gray-400 bg-gray-800 text-gray-100 focus:outline-none focus:ring-1 focus:ring-bl-accent focus:border-bl-accent transition-colors"
                />
                <p className="mt-1 text-xs text-gray-400">
                  Lower numbers appear first. Leave empty to add at the end.
                </p>
              </div>
              <div className="flex gap-3 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowCategoryModal(false);
                    setCategoryForm({ name: "", display_order: "" });
                    setCategoryError("");
                  }}
                  className="px-4 py-2 text-sm font-normal text-gray-300 bg-gray-800 border border-gray-700 rounded-md hover:bg-bl-accent/10 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={categoryLoading}
                  className="px-4 py-2 text-sm font-normal text-white bg-bl-accent hover:bg-bl-accent-light rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {categoryLoading ? "Creating..." : "Create Category"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Feedback;
